import { Form, Formik, useFormikContext } from "formik";
import React, { useEffect, useState } from "react";
import {
  profileBoxInitialValues,
  profileBoxValidationSchema,
} from "./children/ProfileBoxHelpers";
import InnerForm from "./children/InnerForm";
import { useUserUpdateProfile } from "../../../../../services/mutations";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { editUserProfileAction } from "../../../../../redux/actions";
import { updateProfile as updateProfileReducer } from "../../../../../redux/reducers/gallery";
import { useSelector } from "react-redux";
import SaveChangesModal from "../../../../SaveChangesModal";
import { isEmpty } from "lodash";
import { Grid } from "@mui/material";

const ProfileBox = ({
  svChOpen,
  svChOpenSetter,
  onClose,
  initialValues,
  pendingChangesSetter,
  pendingChanges,
}) => {
  const [apiLoading, setApiLoading] = useState(false);
  const dispatch = useDispatch();

  const FormStateWatcher = () => {
    const { dirty, submitCount, touched } = useFormikContext();

    useEffect(() => {
      if (!isEmpty(touched) && dirty && submitCount < 1) {
        pendingChangesSetter(true);
      } else {
        pendingChangesSetter(false);
      }
    }, [touched, dirty, submitCount]);
    return null;
  };
  const updateProfile = useUserUpdateProfile();

  const onSubmitHandler = async (values, formikHelpers) => {
    formikHelpers.setSubmitting(true);
    setApiLoading(true);

    // Build username: prefer entered username, otherwise derive from first+last
    const rawUsername =
      values.username && values.username.trim().length > 0
        ? values.username.trim()
        : `${
            values.firstName.charAt(0).toUpperCase() + values.firstName.slice(1)
          }${
            values.lastName.charAt(0).toUpperCase() + values.lastName.slice(1)
          }`;

    const sanitizedUsername = rawUsername.replace(/\s+/g, "").slice(0, 30);

    const payload = {
      first_name: values.firstName,
      last_name: values.lastName,
      username: sanitizedUsername,
      // show_wheel_number: values.showNumberOfWheels,
      user_profile: {
        bio: values.bio,
        profile_image: values.profile_image,
      },
    };

    updateProfile.mutate(payload, {
      onSuccess: (response) => {
        setApiLoading(false);
        // Update both the profile reducer and dispatch the action
        dispatch(updateProfileReducer({ updates: response?.data }));
        dispatch(editUserProfileAction(response?.data));
        formikHelpers.resetForm({ values });
        formikHelpers.setSubmitting(false);
        toast.success("Profile updated successfully!");
        pendingChangesSetter(false);
        onClose(true);
      },
      onError: (err) => {
        setApiLoading(false);
        formikHelpers.setSubmitting(false);

        const data = err?.response?.data;

        if (data) {
          // If API returns simple message string
          if (typeof data === "string") {
            toast.error(data);
            return;
          }

          // If API returns { message: '...' }
          if (typeof data.message === "string") {
            toast.error(data.message);
            return;
          }

          // If API returns field errors like { username: ['msg'] }
          for (const key of Object.keys(data)) {
            const val = data[key];
            if (Array.isArray(val) && val.length > 0) {
              toast.error(`${key}: ${val.join(" ")}`);
              return;
            }
            if (typeof val === "string" && val.trim().length > 0) {
              toast.error(`${key}: ${val}`);
              return;
            }

            if (typeof val === "object" && val !== null) {
              // try nested
              for (const k2 of Object.keys(val)) {
                const v2 = val[k2];
                if (Array.isArray(v2) && v2.length > 0) {
                  toast.error(`${key}.${k2}: ${v2.join(" ")}`);
                  return;
                }
                if (typeof v2 === "string" && v2.trim().length > 0) {
                  toast.error(`${key}.${k2}: ${v2}`);
                  return;
                }
              }
            }
          }
        }

        // Fallback
        toast.error(
          err?.message || "Failed to update profile. Please try again."
        );
      },
    });
  };

  return (
    <Formik
      initialValues={initialValues || profileBoxInitialValues}
      onSubmit={onSubmitHandler}
      //enableReinitialize
      validateOnMount
      validationSchema={profileBoxValidationSchema}
    >
      {({ submitForm, dirty, submitCount, touched }) => (
        <Form>
          <FormStateWatcher />
          {pendingChanges && (
            <SaveChangesModal
              isOpen={svChOpen}
              title={"Save changes?"}
              subtitle={
                "You have unsaved changes. Do you want to save them before closing ?"
              }
              onClose={() => {
                svChOpenSetter(false);
              }}
              onSave={() => {
                submitForm();
                pendingChangesSetter(false);
                onClose(true);
              }}
              onLeave={() => {
                pendingChangesSetter(false);
                onClose(true);
              }}
            />
          )}
          <Grid
            container
            sx={{
              height: "86vh",
            }}
          >
            <Grid
              xs={12}
              item
              style={{
                height: "calc(100% - 80px)",
              }}
              sx={{
                overflowY: "auto !important",
                flexShrink: 0,
              }}
            >
              <InnerForm
                onClose={() => {
                  if (dirty && submitCount < 1 && !isEmpty(touched)) {
                    onClose(false);
                    pendingChangesSetter(true);
                  } else {
                    onClose(true);
                  }
                }}
                apiLoading={apiLoading}
              />
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default ProfileBox;
